import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";

export const REVIEW_DOCUMENT_KEYS = [
  "government_id", "professional_license", "tb_test", "cpr_bls",
];

export function documentReviewVersion(item = {}) {
  return JSON.stringify([item.fileUrl || "", item.uploadedAt || "", item.fileName || "", item.storagePath || "", item.storageGeneration || ""]);
}

// Keep the decision and its tenant-scoped audit record atomic. Re-read the
// worker so a stale review cannot approve a replacement or overwrite another review.
export async function saveWorkerDocumentReview(db, {
  worker, documentKeys, status, reason = "", actor,
}) {
  if (!worker?.uid || !worker.tenantId || !actor?.uid) {
    throw new Error("Worker identity, tenant and signed-in reviewer are required.");
  }
  if (!["approved", "reupload_required"].includes(status) ||
      !documentKeys?.length || documentKeys.some(key => !REVIEW_DOCUMENT_KEYS.includes(key))) {
    throw new Error("Invalid document review action.");
  }
  const rejectionReason = String(reason).trim();
  if (status === "reupload_required" && !rejectionReason) {
    throw new Error("Enter a reason so the worker knows what to replace.");
  }
  const userRef = doc(db, "users", worker.uid);
  const logRef = doc(collection(db, "adminLogs"));
  const reviewedBy = actor.email || actor.uid;
  const openedDocs = worker.credentialMap || worker.documents || worker.uploadedDocs || {};
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(userRef);
    if (!snapshot.exists()) throw new Error("Worker profile no longer exists.");
    const latest = snapshot.data();
    if (latest.tenantId !== worker.tenantId) {
      throw new Error("Worker tenant changed. Refresh before reviewing documents.");
    }
    const docs = latest.credentialMap || latest.documents || latest.uploadedDocs || {};
    const nextDocs = { ...docs };
    for (const key of documentKeys) {
      const item = docs[key] || {};
      if (documentReviewVersion(item) !== documentReviewVersion(openedDocs[key])) {
        throw new Error("Document changed. Open and review the current file before approving.");
      }
      if (status === "approved" && !item.fileUrl) {
        throw new Error("No uploaded file found. Worker must upload this document first.");
      }
      nextDocs[key] = { ...item, status, verified: status === "approved",
        reviewedAt: new Date().toISOString(), reviewedBy, reviewedByUid: actor.uid,
        rejectionReason: status === "reupload_required" ? rejectionReason : "" };
    }
    const needsCorrection = REVIEW_DOCUMENT_KEYS.some(key =>
      ["reupload_required", "rejected"].includes(nextDocs[key]?.status));
    const allApproved = REVIEW_DOCUMENT_KEYS.every(key =>
      nextDocs[key]?.status === "approved" && nextDocs[key]?.verified === true && nextDocs[key]?.fileUrl);
    const patch = {
      credentialMap: nextDocs,
      onboardingCompleted: false,
      documentsApproved: allApproved,
      onboardingStatus: needsCorrection ? "action_required" : allApproved ? "ready_for_approval" : "submitted",
      complianceStatus: needsCorrection ? "reupload_required" : allApproved ? "docs_approved" : "pending_review",
      status: needsCorrection ? "action_required" : "under_review",
      workerStatus: needsCorrection ? "action_required" : "under_review",
      workerOperationalState: needsCorrection ? "ACTION_REQUIRED" : "UNDER_REVIEW",
      updatedAt: serverTimestamp(),
    };
    if (allApproved) {
      patch.documentsApprovedAt = serverTimestamp();
      patch.documentsApprovedBy = reviewedBy;
    }
    transaction.update(userRef, patch);
    transaction.set(logRef, {
      tenantId: latest.tenantId,
      action: documentKeys.length > 1 ? "approve_all_documents" : "document_review",
      workerUid: worker.uid, workerEmail: latest.email || "",
      documentKeys, reviewStatus: status, reviewedBy, reviewedByUid: actor.uid,
      rejectionReason: status === "reupload_required" ? rejectionReason : "",
      createdAt: serverTimestamp(),
    });
    return { allApproved, needsCorrection };
  });
}
